import { addDays, format, parseISO } from 'date-fns';

import agent from 'api/agent';
import {
    DATE_FORMAT,
    findTakenTable,
    toWireDateTime,
    WIRE_DATE_TIME_FORMAT,
} from 'helpers/reservationHelper';

export type ReservationStatus =
    | 'Requested'
    | 'Confirmed'
    | 'Seated'
    | 'Declined'
    | 'Archived'
    | 'Cancelled';

export type ReservationChannel = 'WebApp' | 'Staff';

export type StaffReservationChannel = 'Phone' | 'Email';

export interface ReservationDTO {
    id: string;
    restaurantId: string;
    status: ReservationStatus;
    channel: ReservationChannel;
    reservationStartDateTime: string;
    reservationEndDateTime: string;
    reservationDurationInMinutes: number;
    guestsNumber: number;
    isGuestsNumberApproximate: boolean;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
    allergies: string[];
    otherAllergies: string | null;
    clientNote: string | null;
    tables: string[];
    createDate: string;
    confirmedAt: string | null;
}

export interface UpdateReservationModel {
    reservationStartDateTime: string;
    reservationDurationInMinutes: number;
    guestsNumber: number;
    isGuestsNumberApproximate: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    email: string | null;
    allergies: string[];
    otherAllergies: string;
    clientNote: string;
    tables: string[];
}

interface ReservationResultDTO {
    reservation: ReservationDTO;
    isOutsideOpeningHours: boolean;
    hasDuplicateGuest: boolean;
}

export interface TableAvailabilityDTO {
    tableId: string;
    isBooked: boolean;
    bookedFrom: string | null;
    hasActiveOrder: boolean;
}

export type SaveRequestResult =
    | { status: 'saved'; reservation: ReservationDTO }
    | { status: 'alreadyHandled' };

export type ConfirmRequestResult =
    | { status: 'confirmed'; reservation: ReservationDTO }
    | { status: 'tableTaken'; tableId: string }
    | { status: 'alreadyHandled' };

export type DeclineRequestResult = 'declined' | 'alreadyHandled';

export type CreateReservationResult =
    | { status: 'created'; reservation: ReservationDTO }
    | { status: 'tableTaken'; tableId: string };

const BASE = '/Reservation';

interface ConflictBody {
    StatusCode: number;
    Message: string;
}

function isConflict(error: unknown): error is ConflictBody {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as Partial<ConflictBody>).StatusCode === 409
    );
}

function getList(
    statuses: ReservationStatus[],
    from?: string,
    to?: string
): Promise<ReservationDTO[]> {
    const params = new URLSearchParams();
    if (from) {
        params.append('From', from);
    }
    if (to) {
        params.append('To', to);
    }
    statuses.forEach((status) => params.append('Statuses', status));
    return agent.requests.get(`${BASE}/list`, { params });
}

function findTakenTableAfterConflict(
    model: UpdateReservationModel,
    excludeReservationId: string | null
): Promise<string | undefined> {
    return Reservations.getTablesAvailability(
        model.reservationStartDateTime,
        model.reservationDurationInMinutes,
        excludeReservationId
    )
        .catch((): TableAvailabilityDTO[] => [])
        .then((availability) => findTakenTable(model.tables, availability));
}

export const Reservations = {
    getRequests: (): Promise<ReservationDTO[]> => getList(['Requested']),

    getActiveReservations: (day: string | null): Promise<ReservationDTO[]> =>
        getList(
            ['Confirmed', 'Seated'],
            toWireDateTime(day ?? format(new Date(), DATE_FORMAT), '00:00'),
            day
                ? toWireDateTime(format(addDays(parseISO(day), 1), DATE_FORMAT), '00:00')
                : undefined
        ),

    getTablesAvailability: (
        start: string,
        durationMinutes: number,
        excludeReservationId: string | null
    ): Promise<TableAvailabilityDTO[]> =>
        agent.requests.get(`${BASE}/tables-availability`, {
            params: { start, durationMinutes, excludeReservationId },
        }),

    saveRequest: (id: string, model: UpdateReservationModel): Promise<SaveRequestResult> =>
        agent.requests
            .put(`${BASE}/${id}`, model, undefined)
            .then(
                (result: ReservationResultDTO): SaveRequestResult => ({
                    status: 'saved',
                    reservation: result.reservation,
                })
            )
            .catch((error: unknown): Promise<SaveRequestResult> | SaveRequestResult =>
                isConflict(error) ? { status: 'alreadyHandled' } : Promise.reject(error)
            ),

    confirmRequest: (id: string, model: UpdateReservationModel): Promise<ConfirmRequestResult> =>
        agent.requests
            .post(`${BASE}/${id}/confirm`, model)
            .then(
                (result: ReservationResultDTO): ConfirmRequestResult => ({
                    status: 'confirmed',
                    reservation: result.reservation,
                })
            )
            .catch(async (error: unknown): Promise<ConfirmRequestResult> => {
                if (!isConflict(error)) {
                    throw error;
                }
                const tableId = await findTakenTableAfterConflict(model, id);
                return tableId ? { status: 'tableTaken', tableId } : { status: 'alreadyHandled' };
            }),

    declineRequest: (id: string): Promise<DeclineRequestResult> =>
        agent.requests
            .post(`${BASE}/${id}/decline`)
            .then((): DeclineRequestResult => 'declined')
            .catch((error: unknown): Promise<DeclineRequestResult> | DeclineRequestResult =>
                isConflict(error) ? 'alreadyHandled' : Promise.reject(error)
            ),

    createReservation: (model: UpdateReservationModel): Promise<CreateReservationResult> =>
        agent.requests
            .post(`${BASE}/create`, model)
            .then(
                (result: ReservationResultDTO): CreateReservationResult => ({
                    status: 'created',
                    reservation: result.reservation,
                })
            )
            .catch(async (error: unknown): Promise<CreateReservationResult> => {
                if (!isConflict(error)) {
                    throw error;
                }
                const tableId = await findTakenTableAfterConflict(model, null);
                if (!tableId) {
                    throw error.Message;
                }
                return { status: 'tableTaken', tableId };
            }),
};
